// The chrome every slide shares (`demo-presentation-shell` task 4.2), reskinned to the company
// brand by the 2026-08-11 presentation-polish pass: a flat brand-cream ground and Figtree, both
// published as `--deck-*` custom properties on this element's own root (`deck-theme.ts` explains
// why the values live in a module rather than a stylesheet, and why they are inline here — the game
// keeps its Field Journal look and nothing in the deck may reach it). Spacing and type sizes come
// from the deck's own projected scale (`scale.ts`), which is deliberately not a kit scale.
//
// Three things the polish pass removed, all for the same reason — the presenter says the idea and
// the slide shows it: the `title` prop and its honey accent rule (user story 2), the honey radial
// wash (flat ground), and every box shadow in the deck (Mermaid cannot draw one, and a
// half-shadowed deck reads worse than a flat one).

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, MutableRefObject, ReactElement, ReactNode } from 'react';
import { SLIDE_FRAME, SLIDE_SPACE } from './scale';
import { DECK_CSS_VARS } from './deck-theme';
import { FESTIVAL_ASSETS } from '../festival-assets';
import { Z_LOCAL } from '../z-layers';
import './slides.css';

/**
 * Dispatched by a slide whose content arrives *after* mount — today that is only a Mermaid diagram,
 * whose SVG lands from a dynamic `import()` some frames later. `useFitScale` measures on mount, so
 * without this the diagram slides paint at the wrong scale until the next window resize.
 */
export const SLIDE_REFIT_EVENT = 'demo-slide-refit';

/** Ask every mounted `SlideFrame` to re-measure. A window event rather than a context or a ref
 * chain: there is exactly one slide mounted at a time and the frame already listens to `resize`, so
 * this is one more listener on a box that already has one. */
export function requestSlideRefit(): void {
  window.dispatchEvent(new Event(SLIDE_REFIT_EVENT));
}

/**
 * **Fit the projected slide to the screen it is actually on** (2026-08-10).
 *
 * Every size in `scale.ts` — and every padding, cap and row height in the slide components — is
 * tuned at 1920×1080 (see `DColumn`'s row heights, historically). On a smaller window those px
 * stayed put while the box shrank, so text overflowed the frame and the diagram rows wrapped at
 * points nobody tuned. Shrinking only the *type* scale would not have fixed it: the fixed caps
 * beside it decide where a label wraps, so type and layout have to move together or the wrap points
 * drift anyway.
 *
 * So the slide is always laid out at exactly 1920×1080 and the whole box is scaled to fit, the way
 * a projector fits a deck. `min(w/W, h/H)` letterboxes rather than crops; the frame's ground sits
 * behind at full bleed, so the letterbox is invisible.
 */
function useFitScale(): [MutableRefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const fit = (): void => {
      const box = ref.current?.getBoundingClientRect();
      // An unlaid-out box (jsdom reports 0×0) keeps the 1× default rather than collapsing the
      // slide to nothing — the same "degrade to the tuned size" posture as a missing screen.
      if (box === undefined || box.width === 0 || box.height === 0) {
        return;
      }
      setScale(Math.min(box.width / SLIDE_FRAME.width, box.height / SLIDE_FRAME.height));
    };
    fit();
    // The frame fills the (fullscreen) viewport, so a window resize is the only thing that changes
    // its box — no ResizeObserver needed, and jsdom has this event where it has no observer. The
    // refit event covers the one case a resize does not: content that arrives after mount.
    window.addEventListener('resize', fit);
    window.addEventListener(SLIDE_REFIT_EVENT, fit);
    return () => {
      window.removeEventListener('resize', fit);
      window.removeEventListener(SLIDE_REFIT_EVENT, fit);
    };
  }, []);
  return [ref, scale];
}

export interface SlideFrameProps {
  children: ReactNode;
  /** Centers the stack both ways — the cover, apiophobia and wrap-up slides that read as one
   * centred statement rather than a body filling the frame. */
  center?: boolean;
  festivalBackdrop?: boolean;
}

export function SlideFrame({
  children,
  center = false,
  festivalBackdrop = false,
}: SlideFrameProps): ReactElement {
  const [frameRef, fitScale] = useFitScale();
  return (
    <div
      ref={frameRef}
      data-testid="demo-slide-frame"
      style={
        {
          ...DECK_CSS_VARS,
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          background: 'var(--deck-bg)',
          fontFamily: 'var(--font-body)',
          fontWeight: 500,
          color: 'var(--deck-ink)',
          boxSizing: 'border-box',
        } as CSSProperties
      }
    >
      {festivalBackdrop && (
        <img
          data-testid="festival-full-background"
          src={FESTIVAL_ASSETS.backgroundFull}
          alt=""
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      {/* The scaler: a fixed 1920×1080 slide, centred in the frame and shrunk to fit it. The
          entrance animation stays on the stack *inside* it, because `demo-slide-in`'s keyframes
          animate `transform` and would otherwise clobber this scale. */}
      <div
        style={{
          position: 'absolute',
          zIndex: Z_LOCAL.slideContent,
          left: '50%',
          top: '50%',
          width: SLIDE_FRAME.width,
          height: SLIDE_FRAME.height,
          transform: `translate(-50%, -50%) scale(${fitScale})`,
        }}
      >
        <div
          className="demo-slide-in"
          style={{
            position: 'relative',
            zIndex: Z_LOCAL.slideContent,
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: center ? 'center' : 'stretch',
            justifyContent: 'center',
            textAlign: center ? 'center' : 'left',
            padding: `${SLIDE_SPACE.padY}px ${SLIDE_SPACE.padX}px`,
            gap: SLIDE_SPACE.gap,
          }}
        >
          {/* A body slide fills the 16:9 frame and centers itself in it, rather than stacking at
              the top with the rest of the frame empty (E2E finding P2). A `center` slide is already
              one centered group via the outer box above, so it renders `children` directly rather
              than through a second nested centering box.
              **This branch used to key on `title === undefined`**, which is why it moved to `center`
              when the titles went: the five ex-titled slides still need the centered body, and the
              `center` slides still need the direct children. */}
          {center ? (
            children
          ) : (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: SLIDE_SPACE.gap,
              }}
            >
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function DeathMatchFrame({
  children,
  center = false,
}: SlideFrameProps): ReactElement {
  const [frameRef, fitScale] = useFitScale();
  return (
    <div
      ref={frameRef}
      data-testid="demo-slide-frame"
      style={
        {
          ...DECK_CSS_VARS,
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          background: 'var(--deck-deathmatch)',
          fontFamily: 'var(--font-body)',
          fontWeight: 500,
          color: 'var(--deck-ink)',
          boxSizing: 'border-box',
        } as CSSProperties
      }
    >
      {/* The scaler: a fixed 1920×1080 slide, centred in the frame and shrunk to fit it. The
          entrance animation stays on the stack *inside* it, because `demo-slide-in`'s keyframes
          animate `transform` and would otherwise clobber this scale. */}
      <div
        style={{
          position: 'absolute',
          zIndex: Z_LOCAL.slideContent,
          left: '50%',
          top: '50%',
          width: SLIDE_FRAME.width,
          height: SLIDE_FRAME.height,
          transform: `translate(-50%, -50%) scale(${fitScale})`,
        }}
      >
        <div
          className="demo-slide-in"
          style={{
            position: 'relative',
            zIndex: Z_LOCAL.slideContent,
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: center ? 'center' : 'stretch',
            justifyContent: 'center',
            textAlign: center ? 'center' : 'left',
            padding: `${SLIDE_SPACE.padY}px ${SLIDE_SPACE.padX}px`,
            gap: SLIDE_SPACE.gap,
          }}
        >
          {/* A body slide fills the 16:9 frame and centers itself in it, rather than stacking at
              the top with the rest of the frame empty (E2E finding P2). A `center` slide is already
              one centered group via the outer box above, so it renders `children` directly rather
              than through a second nested centering box.
              **This branch used to key on `title === undefined`**, which is why it moved to `center`
              when the titles went: the five ex-titled slides still need the centered body, and the
              `center` slides still need the direct children. */}
          {center ? (
            children
          ) : (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: SLIDE_SPACE.gap,
              }}
            >
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}